import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {
    BehaviorSubject,
    distinctUntilChanged,
    finalize,
    map,
    Observable,
    of,
    retry,
    shareReplay,
    startWith,
    Subject,
    take,
    throwError,
    timer
} from 'rxjs';
import {GenericResponse} from '../_types/shared';
import {
    Category,
    CommentFlare,
    Community,
    CommunityPage,
    Domain,
    NewQuestion,
    NewSubGroupPayload,
    Question,
    SubGroup,
    UserAccessLevel,
    UserCommunity
} from '../_types/communities';
import {fetchAllPages} from '../_utils/http-params';
import {User, UserWithModerationInfo} from '../_types/user';
import {AuthService} from './auth.service';
import {catchError} from 'rxjs/operators';
import {CommunityStatistic, UserStats} from "../_types/content";
import {LocalStorageService} from "./local-storage.service";
import {ThemeService} from "./theme.service";
import {RewardsService} from "./rewards.service";

@Injectable({
    providedIn: 'root'
})
export class CommunitiesService {
    backgroundImage: string = '';
    backgroundImageChange: Subject<string> = new Subject<string>();
    private api = `${environment.apiUrl}/communities`;
    private cacheCommunities$: Observable<Community[]> | null = null;
    private cacheUserCommunities$: Observable<UserCommunity[]> | null = null;
    private cacheUserList$: Observable<UserWithModerationInfo[]> | null = null;
    private cacheCommentFlares$: Observable<CommentFlare[]> | null = null;
    private cacheCommunityPages$: Observable<CommunityPage[]> | null = null;

    private currentDashboardCommunity$: BehaviorSubject<Community | null> = new BehaviorSubject<Community | null>(null);
    private cacheCommunityStats$: Map<string, Observable<CommunityStatistic>> = new Map();
    private cacheUserStats$: Map<string, Observable<UserStats>> = new Map();

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private localStorageService: LocalStorageService,
        private themeService: ThemeService,
        private rewardService: RewardsService
    ) {
        this.currentDashboardCommunity$.next(this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null));

        this.backgroundImageChange.subscribe((value: string): void => {
            this.backgroundImage = value
        });

        this.currentDashboardCommunity$.subscribe((community: Community | null): void => {
            const favicon32 = document.getElementById("favicon-32") as HTMLLinkElement | null;
            const favicon16 = document.getElementById("favicon-16") as HTMLLinkElement | null;
            const touchIcon = document.getElementById("apple-touch-icon") as HTMLLinkElement | null;

            if (favicon32) {
                favicon32.href = community?.logo_32px || environment.favicon32;
            }

            if (favicon16) {
                favicon16.href = community?.logo_16px || environment.favicon16;
            }

            if (touchIcon) {
                touchIcon.href = community?.logo_180px || environment.appleTouchIcon;
            }
        });
    }

    private _emailVerified!: boolean;

    get emailVerified(): boolean {
        return this._emailVerified;
    }

    set emailVerified(value: boolean) {
        this._emailVerified = value;
    }

    private _profileRefreshed!: boolean;

    get profileRefreshed(): boolean {
        return this._profileRefreshed;
    }

    set profileRefreshed(value: boolean) {
        this._profileRefreshed = value;
    }

    getCommunityPages(id: number): Observable<CommunityPage[]> {
        if (this.cacheCommunityPages$) {
            return this.cacheCommunityPages$;
        }
        let params: HttpParams = new HttpParams();
        this.cacheCommunityPages$ = fetchAllPages<CommunityPage>(
            this.http,
            `${environment.apiUrl}/communities/${id}/pages/`,
            1000,
            params
        ).pipe(
            catchError(error => {
                console.error('Pages collection failed', error);
                return of([]);
            }),
            shareReplay({bufferSize: 1, refCount: true, windowTime: 60000}),
            finalize(() => {
                this.cacheCommunityPages$ = null;
            })
        );
        return this.cacheCommunityPages$;
    }

    getCommunityPage(communityId: number, pageId: number): Observable<CommunityPage> {
        return this.http.get<GenericResponse<any>>(`${environment.apiUrl}/communities/${communityId}/pages/${pageId}`).pipe(map(res => res.data));
    }

    getCurrentDashboardCommunity(): Observable<Community | null> {
        return this.currentDashboardCommunity$
            .pipe(
                startWith(this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null)),
                distinctUntilChanged(this.customComparator)
            );
    }

    userHasCommunityRole(roles: UserAccessLevel[]): boolean {
        const community: Community | null = this.currentDashboardCommunity$.value;
        return !!community?.user_membership && roles.includes(community?.user_membership?.access_level);
    }

    setCurrentDashboardCommunity(community: Community | null): void {

        // Clear community-specific caches
        this.rewardService.cacheCurrentUserStreaks$ = null;
        this.rewardService.cacheUserStreaks$.clear();
        this.rewardService.cacheStreaks$ = null;
        this.cacheUserList$ = null;
        this.cacheUserStats$.clear();
        this.cacheCommunityStats$.clear();
        this.cacheCommentFlares$ = null;
        this.cacheCommunityPages$ = null;

        this.localStorageService.setItem(community, 'lastSelectedCommunity')

        if (community?.main_color_hex) {
            this.themeService.savePrimaryColor(community?.main_color_hex)
        } else {
            this.themeService.savePrimaryColor(environment.primaryColor || '#1B9AAA')
        }

        if (community?.accent_color_hex) {
            this.themeService.saveAccentColor(community.accent_color_hex);
        } else {
            this.themeService.saveAccentColor(environment.accentColor || '#c93457');
        }

        if (community?.logo_background_color_hex) {
            document.documentElement.style.setProperty(
                `--logo-background`, community?.logo_background_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--logo-background`, 'transparent'
            );
        }

        if (community?.main_background_color_hex) {
            document.documentElement.style.setProperty(
                `--body-background-colour`, community?.main_background_color_hex
            );
        }

        if (community?.notification_badge_color_hex) {
            document.documentElement.style.setProperty(
                `--badge-background-color`, community?.notification_badge_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--badge-background-color`, 'var(--theme-accent-500)'
            );
        }

        if (community?.audio_background_color) {
            document.documentElement.style.setProperty(
                `--audio-background-color`, community?.audio_background_color
            );
        } else {
            document.documentElement.style.setProperty(
                `--audio-background-color`, 'var(--primary)'
            );
        }
        if (community?.audio_foreground_color) {
            document.documentElement.style.setProperty(
                `--audio-foreground-color`, community?.audio_foreground_color
            );
        } else {
            document.documentElement.style.setProperty(
                `--audio-foreground-color`, 'var(--accent)'
            );
        }

        if (community?.body_font_type?.name) {
            document.documentElement.style.setProperty(
                `--font-family`, "'" + community?.body_font_type?.name + "'"
            );
        } else {
            document.documentElement.style.setProperty(
                `--font-family`, 'roboto'
            );
        }

        if (community?.heading_font_type?.name) {
            document.documentElement.style.setProperty(
                `--heading-font-family`, "'" + community?.heading_font_type?.name + "'"
            );
        } else {
            document.documentElement.style.setProperty(
                `--heading-font-family`, 'roboto'
            );
        }


        if (community?.body_font_size) {
            document.documentElement.style.setProperty(
                `--body-font-size`, JSON.stringify(community?.body_font_size) + 'em'
            );
        } else {
            document.documentElement.style.setProperty(
                `--body-font-size`, '1em'
            );
        }

        if (community?.poll_vote_gradient_start_color_hex) {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-start-color-hex`, community?.poll_vote_gradient_start_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-start-color-hex`, '#003247'
            );
        }

        if (community?.poll_vote_gradient_end_color_hex) {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-end-color-hex`, community?.poll_vote_gradient_end_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--poll-vote-gradient-end-color-hex`, '#931772'
            );
        }

        if (community?.registration_progress_highlight_color_hex) {
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-background-color`, community?.registration_progress_highlight_color_hex
            );
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-foreground-color`, this.themeService.getReadableColor(community?.registration_progress_highlight_color_hex)
            );
        } else {
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-background-color`, environment.primaryColor || '#1B9AAA'
            );
            document.documentElement.style.setProperty(
                `--mat-stepper-header-selected-state-icon-foreground-color`, this.themeService.getReadableColor(environment.primaryColor || '#1B9AAA')
            );
        }


        if (community?.button_radius) {
            document.documentElement.style.setProperty(
                `--mat-button-filled-container-shape`, JSON.stringify(community?.button_radius) + 'px'
            );
            document.documentElement.style.setProperty(
                `--mat-button-toggle-shape`, JSON.stringify(community?.button_radius) + 'px'
            );
            document.documentElement.style.setProperty(
                `--mat-button-protected-container-shape`, JSON.stringify(community?.button_radius) + 'px'
            );
        } else {
            document.documentElement.style.setProperty(
                `--mat-button-filled-container-shape`, '5px'
            );
            document.documentElement.style.setProperty(
                `--mat-button-toggle-shape`, '5px'
            );
            document.documentElement.style.setProperty(
                `--mat-button-protected-container-shape`, '5px'
            );
        }


        if (community?.recent_activity_highlight_color_hex) {
            document.documentElement.style.setProperty(
                `--recent-activity-highlight-colour`, community?.recent_activity_highlight_color_hex
            );
        } else {
            document.documentElement.style.setProperty(
                `--recent-activity-highlight-colour`, 'var(--primary)'
            );
        }
        if (community?.home_page_path) {
            this.localStorageService.setItem(community?.home_page_path, 'homePath')
        } else {
            this.localStorageService.removeItem('homePath')
        }

        if (community?.login_header_color_hex) {
            document.documentElement.style.setProperty(
                `--unAuthMenuBackground`, community?.login_header_color_hex
            );
            document.documentElement.style.setProperty(
                `--unAuthMenuForeground`, this.themeService.getReadableColor(community?.login_header_color_hex)
            );
        } else if (community?.dark_header) {
            document.documentElement.style.setProperty(
                `--unAuthMenuBackground`, 'black'
            );
            document.documentElement.style.setProperty(
                `--unAuthMenuForeground`, 'white'
            );
        } else if (community?.main_color_hex) {
            document.documentElement.style.setProperty(
                `--unAuthMenuBackground`, community?.main_color_hex
            );
            document.documentElement.style.setProperty(
                `--unAuthMenuForeground`, this.themeService.getReadableColor(community?.main_color_hex)
            );
        } else {
            document.documentElement.style.setProperty(
                `--unAuthMenuBackground`, 'white'
            );
            document.documentElement.style.setProperty(
                `--unAuthMenuForeground`, 'black'
            );
        }
        this.currentDashboardCommunity$.next(community);
        this.themeService.updateCurrentBrightnessTheme();
    }

    getCommunityStatistic(communityId: number, end_date?: string, start_date?: string): Observable<CommunityStatistic> {
        const id = `${communityId}_${start_date}_${end_date}`;
        if (!this.cacheCommunityStats$.has(id)) {
            let params = new HttpParams().set('community_id', communityId);
            if (start_date) {
                params = params.append('start_date', start_date);
            }
            if (end_date) {
                params = params.append('end_date', end_date);
            }
            const stats$ = this.http.get<GenericResponse<CommunityStatistic>>(`${environment.apiUrl}/activity/community-stats`, {params}).pipe(
                finalize(() => timer(1000).pipe(take(1)).subscribe(() => this.cacheCommunityStats$.delete(id))),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map(response => response.data)
            );
            this.cacheCommunityStats$.set(id, stats$);
        }
        return this.cacheCommunityStats$.get(id)!;
    }

    userStats(communityId: number, user_id: number, end_date?: string, start_date?: string): Observable<UserStats> {
        const id = `${communityId}_${start_date}_${end_date}`;
        if (!this.cacheUserStats$.has(id) && user_id) {
            let params = new HttpParams().set('community_id', communityId).append('user_id', user_id);
            if (start_date) {
                params = params.append('start_date', start_date);
            }
            if (end_date) {
                params = params.append('end_date', end_date);
            }
            const userStats$ = this.http.get<GenericResponse<UserStats>>(`${environment.apiUrl}/activity/user-stats`, {params}).pipe(
                finalize(() => timer(1000).pipe(take(1)).subscribe(() => this.cacheUserStats$.delete(id))),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map(response => response.data)
            );
            this.cacheUserStats$.set(id, userStats$);
        }
        return this.cacheUserStats$.get(id)!;
    }

    changeBackgroundImage(image: string): void {
        this.backgroundImageChange.next(image);
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.userHasCommunityRole(roles);
    }

    getAllCommunities(): Observable<Community[]> {
        if (!this.cacheCommunities$) {
            this.cacheCommunities$ = fetchAllPages<Community>(this.http, `${this.api}/active/`).pipe(
                finalize(() => this.cacheCommunities$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 500})
            );
        }
        return this.cacheCommunities$;
    }

    getSpecificCommunity(id?: number): Promise<any> {
        return new Promise((resolve, reject): void => {
            let parameters: HttpParams = new HttpParams();
            let url: string = window.location.origin;
            url = url.toLowerCase()
            // url = 'https://nebula.explainonline.co.uk/';
            parameters = parameters.set('retired', true);
            const MAX_RETRIES = 3;

            let communityId: number | null = id ? id : null;
            if (!communityId) {
                parameters = parameters.set('front_end_url', url);
            }

            fetchAllPages<any>(this.http, `${this.api}/`, 1000, parameters)
                .pipe(
                    retry(MAX_RETRIES),
                    catchError((error): Observable<never> => {
                        console.error(`Request failed after ${MAX_RETRIES} attempts`, error);
                        return throwError(() => new Error('Request failed after multiple retries'));
                    })
                )
                .subscribe({
                    next: (data): void => {
                        const currentCommunity = data.find((community): boolean => {
                            if (communityId !== null) {
                                return Number(community.id) === Number(communityId);
                            }

                            const frontEndUrl = community.front_end_url?.toLowerCase();
                            const alternativeUrl =
                                community.alternative_front_end_url?.toLowerCase();

                            return (
                                (frontEndUrl &&
                                    (
                                        url.includes(frontEndUrl) ||
                                        frontEndUrl.includes(url) ||
                                        frontEndUrl === url
                                    )) ||
                                (alternativeUrl &&
                                    (
                                        url.includes(alternativeUrl) ||
                                        alternativeUrl.includes(url) ||
                                        alternativeUrl === url
                                    ))
                            );
                        });


                        if (!currentCommunity) {
                            reject('No Community Found');
                            return;
                        }

                        localStorage.setItem(
                            'lastSelectedCommunity',
                            JSON.stringify(currentCommunity)
                        );

                        currentCommunity.logo
                            ? localStorage.setItem('communityLogo', currentCommunity.logo)
                            : localStorage.removeItem('communityLogo');

                        currentCommunity.name
                            ? localStorage.setItem('communityName', currentCommunity.name)
                            : localStorage.removeItem('communityName');

                        currentCommunity.background_image
                            ? localStorage.setItem(
                                'communityLoginBG',
                                currentCommunity.background_image
                            )
                            : localStorage.removeItem('communityLoginBG');

                        this.changeBackgroundImage(
                            currentCommunity.main_background_image || ''
                        );

                        resolve(currentCommunity);
                    },
                    error: (err): void => {
                        console.error('Final failure:', err);
                        reject('Request failed after multiple retries');
                    }
                });
        });
    }

    getUserCommunities(accessToken?: string): Observable<UserCommunity[]> {
        if (!this.cacheUserCommunities$ || accessToken) {
            if (accessToken && !localStorage.getItem('access_token')) {
                localStorage.setItem('access_token', accessToken);
            }
            this.cacheUserCommunities$ = fetchAllPages<UserCommunity>(this.http, `${this.api}/my-memberships/`).pipe(
                finalize(() => this.cacheUserCommunities$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 500})
            );
        }
        return this.cacheUserCommunities$;
    }

    getAllEligibilityQuestions(): Observable<any> {
        return this.getAllCommunities()
            .pipe(
                map(res =>
                    res.flatMap(r => r.questions?.map(q => ({...q, communityName: r.company_name})) || []))
            );
    }

    getUserSubGroups(communityId: number, pageSize: number = 1000): Observable<SubGroup[]> {
        return fetchAllPages<SubGroup>(this.http, `${this.api}/${communityId}/my-subgroups/`, pageSize);
    }

    getAllSubGroups(communityId: number, pageSize: number = 1000): Observable<SubGroup[]> {
        return fetchAllPages<SubGroup>(this.http, `${this.api}/${communityId}/subgroups/`, pageSize);
    }

    // Getting subgroups depends on user permissions
    getSubGroups(communityId: number, pageSize: number = 1000): Observable<SubGroup[]> {
        return this.authService.isSuperUser() || this.hasCommunityRole(['client', 'full_admin', 'moderator', 'observer']) ? this.getAllSubGroups(communityId, pageSize) : this.getUserSubGroups(communityId, pageSize)
    }


    getSubGroup(community_id: number, subgroup_id: number): Observable<GenericResponse<SubGroup>> {
        return this.http.get<GenericResponse<SubGroup>>(`${this.api}/${community_id}/subgroups/${subgroup_id}/`);
    }

    createSubgroup(communityId: number, payload: NewSubGroupPayload): Observable<SubGroup> {
        return this.http.post<SubGroup>(`${this.api}/${communityId}/subgroups/`, payload);
    }

    updateSubgroup(communityId: number, subgroupId: number, payload: {
        name?: string;
        description?: string;
        order?: number
    }): Observable<SubGroup> {
        return this.http.patch<SubGroup>(`${this.api}/${communityId}/subgroups/${subgroupId}/`, payload);
    }

    deleteSubgroup(communityId: number, subgroupId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/${communityId}/subgroups/${subgroupId}/`);
    }

    getCategories(communityId: number): Observable<Category[]> {
        return fetchAllPages<Category>(this.http, `${this.api}/${communityId}/categories/`);
    }

    createCategory(communityId: number, name: string): Observable<Category> {
        return this.http.post<Category>(`${this.api}/${communityId}/categories/`, {name});
    }

    updateCategory(communityId: number, categoryId: number, name: string): Observable<Category> {
        return this.http.patch<Category>(`${this.api}/${communityId}/categories/${categoryId}/`, {name});
    }

    deleteCategory(communityId: number, categoryId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/${communityId}/categories/${categoryId}/`);
    }

    getUserList(communityId: number): Observable<UserWithModerationInfo[]> {
        if (!this.cacheUserList$) {
            this.cacheUserList$ = fetchAllPages<UserWithModerationInfo>(this.http, `${this.api}/${communityId}/members/`).pipe(
                finalize(() => this.cacheUserList$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 60000})
            );
        }
        return this.cacheUserList$;
    }

    searchUserList(communityId: number, search: string): Observable<UserWithModerationInfo[]> {
        let parameters: HttpParams = new HttpParams();
        parameters = parameters.set('search', search);
        return fetchAllPages<any>(this.http, `${this.api}/${communityId}/members/`, 1000, parameters)
    }

    searchSimpleUserList(communityId: number, search: string): Observable<UserWithModerationInfo[]> {
        let parameters: HttpParams = new HttpParams();
        parameters = parameters.set('search', search);
        return fetchAllPages<any>(this.http, `${this.api}/${communityId}/members-stripped/`, 1000, parameters)
    }

    getUserInformationForCommunity(communityId: number): Observable<UserWithModerationInfo | null> {
        return this.http.get<GenericResponse<UserWithModerationInfo>>(`${this.api}/${communityId}/me/`)
            .pipe(
                map(res => res.data),
                catchError(err => {
                    return err.includes('User is not a member of community') ? of(null) : throwError(() => err);
                }));
    }

    getUserMembership(communityId: number, userId: number): Observable<any> {
        return this.http.get<any>(`${this.api}/${communityId}/member/${userId}/`);
    }

    addUserToSubgroup(communityId: number, subgroupId: number, userId: number[]): Observable<GenericResponse<string>> {
        return this.http.post<GenericResponse<string>>(`${this.api}/${communityId}/subgroups/${subgroupId}/add-members/`, {user_ids: userId});
    }

    removeUserFromSubgroup(communityId: number, subgroupId: number, userId: number): Observable<GenericResponse<string>> {
        return this.http.post<GenericResponse<string>>(`${this.api}/${communityId}/subgroups/${subgroupId}/remove-member/`, {user_id: userId});
    }

    subgroupMembers(communityId: number, subgroupId: number): Observable<User[]> {
        return fetchAllPages<{ user: User }>(this.http, `${this.api}/${communityId}/subgroups/${subgroupId}/members/`)
            .pipe(map(response => response.map(res => res.user)));
    }

    getQuestions(communityId: number): Observable<Question[]> {
        return fetchAllPages<Question>(this.http, `${this.api}/${communityId}/questions/`);
    }

    createNewQuestion(communityId: number, questionData: NewQuestion): Observable<Question> {
        return this.http.post<Question>(`${this.api}/${communityId}/questions/`, questionData);
    }

    updateQuestion(communityId: number, questionId: number, questionData: NewQuestion): Observable<Question> {
        return this.http.patch<Question>(`${this.api}/${communityId}/questions/${questionId}/`, questionData);
    }

    deleteQuestion(communityId: number, questionId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/${communityId}/questions/${questionId}/`);
    }

    editCommunityInviteEmail(communityId: number, template: string, subject?: string): Observable<any> {
        let body: any = {
            custom_invite_email_html: template
        }
        if (subject) {
            body['custom_invite_subject_text'] = subject;
        }
        return this.http.patch<any>(`${environment.apiUrl}/community/details/${communityId}/`, body);
    }

    editCommunityWelcomeEmail(communityId: number, template: string, subject?: string): Observable<any> {
        let body: any = {
            custom_welcome_email_html: template
        }
        if (subject) {
            body['custom_welcome_email_subject_text'] = subject;
        }
        return this.http.patch<any>(`${environment.apiUrl}/community/details/${communityId}/`, body);
    }

    getDomainWhitelist(communityId?: number): Observable<GenericResponse<Domain[]>> {
        let params: HttpParams = new HttpParams();
        if (communityId) {
            params = params.append('community_id', communityId);
        }
        return this.http.get<GenericResponse<Domain[]>>(`${environment.apiUrl}/profile-question-domain-whitelist/`, {params});
    }

    getCommentFlares(communityId: number): Observable<CommentFlare[]> {
        if (this.cacheCommentFlares$) {
            return this.cacheCommentFlares$;
        }
        let params: HttpParams = new HttpParams();
        params = params.append('community_id', communityId);
        this.cacheCommentFlares$ = fetchAllPages<CommentFlare>(
            this.http,
            `${environment.apiUrl}/comment-flares/`,
            1000,
            params
        ).pipe(
            catchError(error => {
                console.error('comment flares failed', error);
                return of([]);
            }),
            shareReplay({bufferSize: 1, refCount: true, windowTime: 60000}),
            finalize(() => {
                this.cacheCommentFlares$ = null;
            })
        );
        return this.cacheCommentFlares$;
    }

    private customComparator(
        prev: Community | null,
        curr: Community | null
    ): boolean {
        if (prev === curr) return true;
        if (!prev || !curr) return false;
        // Publish refreshed feed settings even when the selected community
        // stays the same (for example after editing it in Django admin).
        return prev.id === curr.id
            && prev.vote_button_text === curr.vote_button_text
            && prev.attachment_button_text === curr.attachment_button_text
            && prev.allow_post_button_text_customisation === curr.allow_post_button_text_customisation
            && prev.allow_post_link_button_text_customisation === curr.allow_post_link_button_text_customisation
            && prev.login_page_title_text === curr.login_page_title_text
            && prev.login_heading_help_text === curr.login_heading_help_text
            && prev.registration_heading_help_text === curr.registration_heading_help_text
            && prev.registration_page_title_text_rendered === curr.registration_page_title_text_rendered
            && prev.separate_pinned_posts_by_subgroup === curr.separate_pinned_posts_by_subgroup
            && prev.show_profile_tile_on_feed === curr.show_profile_tile_on_feed;
    }
}

